/**
 * The learning flow shared by every client (Docs/plans/CLOUD_DECKS_PLAN.md). A review shows, in order:
 * cards that are due; if nothing is due, the next words from the learner's "Up next to learn"
 * list; if that is empty, questions about the next words of the deck the learner follows.
 */
export const DEFAULT_NEW_WORDS_PER_DAY = 50;
/** Hidden for good by "I already know this word". */
export function isRetired(card) {
    return !!card.retired_at;
}
/** Chosen but not started yet: a member of "Up next to learn". */
export function isWaiting(card) {
    return card.fsrs_state === 'New' && !isRetired(card);
}
/** A waiting card whose text exists, so it can be shown. */
export function isReadyToLearn(card) {
    return isWaiting(card) && card.content_status !== 'pending';
}
/**
 * Order of "Up next to learn": words the learner added themselves first, then words chosen in
 * decks; within each, the order they were chosen in (for a deck, that is the deck's order).
 */
export function compareUpNext(a, b) {
    const priority = (b.queue_priority ?? 0) - (a.queue_priority ?? 0);
    if (priority)
        return priority;
    const at = (a.queued_at ?? a.created_at).localeCompare(b.queued_at ?? b.created_at);
    return at || a.id.localeCompare(b.id);
}
/** The learner's waiting list, in the order the words will be introduced. */
export function upNextCards(cards) {
    return cards.filter(isWaiting).sort(compareUpNext);
}
/** The card fields that put a newly chosen word on the waiting list. */
export function upNextFields(source, now = new Date()) {
    return {
        queued_at: now.toISOString(),
        queue_priority: source.kind === 'user' ? 1 : 0,
        source_deck_id: source.kind === 'deck' ? source.deckId : null,
    };
}
/** Words learned for the first time since `dayStart` (the learner's local midnight). */
export function newWordsLearnedSince(logs, dayStart) {
    const from = dayStart.toISOString();
    return logs.filter((l) => l.state_before === 'New' && l.reviewed_at >= from).length;
}
/**
 * Plans one review. Due cards come first; only the slots they leave free go to new words, and only
 * the slots those leave free go to questions about the followed deck. Retired cards never appear,
 * and waiting cards whose text is still being prepared are skipped until they are ready.
 */
export function planReview(input) {
    const now = (input.now ?? new Date()).toISOString();
    const size = Math.max(1, Math.floor(input.sessionSize));
    const perDay = Math.max(0, Math.floor(input.newWordsPerDay ?? DEFAULT_NEW_WORDS_PER_DAY));
    const allowedToday = Math.max(0, perDay - Math.max(0, input.newWordsLearnedToday));
    const due = input.cards
        .filter((c) => c.fsrs_state !== 'New' && !isRetired(c) && c.due_date <= now)
        .sort((a, b) => a.due_date.localeCompare(b.due_date) || a.id.localeCompare(b.id))
        .slice(0, size);
    const freeAfterDue = size - due.length;
    const fresh = input.cards
        .filter(isReadyToLearn)
        .sort(compareUpNext)
        .slice(0, Math.min(freeAfterDue, allowedToday));
    const newWordsLeftToday = allowedToday - fresh.length;
    // Questions are only asked once the waiting list has nothing ready left for this review.
    const waitingLeft = input.cards.filter(isReadyToLearn).length - fresh.length;
    const deckQuestions = waitingLeft > 0 ? 0 : Math.min(size - due.length - fresh.length, newWordsLeftToday);
    return { due, fresh, deckQuestions: Math.max(0, deckQuestions), newWordsLeftToday };
}
function byPosition(a, b) {
    return a.position - b.position || a.id.localeCompare(b.id);
}
/**
 * The single rule for where a learner stands on each word of a deck, used by the deck page, the
 * progress bars and the followed-deck questions, so they can never disagree:
 * - known: the word is on the known list, or the learner retired its card;
 * - learning: the learner has a started card for exactly this word;
 * - waiting: the learner chose it, but has not started it yet;
 * - undecided: everything else.
 * `knownWords` holds lower-cased known words (dictionary forms and their other forms).
 */
export function classifyDeckWords(words, cards, knownWords, deferrals = new Map(), 
/**
 * The names a deck word goes by: its own text and, when it is linked to the library, that entry's
 * dictionary form (the deck says "Die Speisekarte", the card and the known list say
 * "Speisekarte"). Defaults to the word's own text.
 */
namesOf = (word) => [word.lemma]) {
    // A card counts for its own word AND meaning ("Bank" the bench is not "Bank" the bank).
    const keyOf = (name, senseKey) => `${name}\u0000${senseKey ?? ''}`;
    const cardByLemma = new Map();
    for (const card of cards) {
        const key = keyOf((card.lemma || card.front_text).trim().toLowerCase(), card.sense_key);
        const existing = cardByLemma.get(key);
        // A live card wins over a retired one for the same word.
        if (!existing || (isRetired(existing) && !isRetired(card)))
            cardByLemma.set(key, card);
    }
    const counts = {
        known: 0,
        learning: 0,
        waiting: 0,
        undecided: 0,
    };
    const classified = [...words].sort(byPosition).map((word) => {
        const keys = [
            ...new Set(namesOf(word)
                .map((name) => name?.trim().toLowerCase())
                .filter((name) => !!name)),
        ];
        const sense = word.sense_key ?? '';
        const cards = keys
            .map((key) => cardByLemma.get(keyOf(key, sense)))
            .filter((c) => !!c);
        // A live card wins over a retired one when the two names point at different cards.
        const card = cards.find((c) => !isRetired(c)) ?? cards[0];
        let status;
        if (card && !isRetired(card))
            status = card.fsrs_state === 'New' ? 'waiting' : 'learning';
        // Known words are kept per spelling and stand for a word's first meaning; a later meaning is
        // known only through its own (put away) card.
        else if (card || (!sense && keys.some((key) => knownWords.has(key))))
            status = 'known';
        else
            status = 'undecided';
        counts[status] += 1;
        const deferredAt = status === 'undecided' ? deferrals.get(word.id) : undefined;
        return { word, status, card, deferredAt };
    });
    const open = classified.filter((c) => c.status === 'undecided');
    const undecided = [
        ...open.filter((c) => !c.deferredAt),
        ...open
            .filter((c) => c.deferredAt)
            .sort((a, b) => a.deferredAt.localeCompare(b.deferredAt) || byPosition(a.word, b.word)),
    ].map((c) => c.word);
    return { words: classified, counts, undecided };
}
